// src/features/analytics/AnalyticsUtils.ts

import { DateTime } from 'luxon';
import { ApiError } from '@/shared/api/ApiError';
import { ANALYTICS_LIVE_STALE_AFTER_SECONDS } from '@/lib/constants/analytics';
import { SECONDS_PER_DAY, type UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsCoverage } from './types';

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;
// Binary base, matching the platform's existing byte convention
// (AccountUtils: 1 GB = 1024^3 bytes on /account/usage).
const BYTES_PER_UNIT = 1024;

export type AnalyticsErrorKind =
  | 'empty'           // no data for the range (range_outside_coverage / product window)
  | 'busy'            // transient overload — retry shortly
  | 'timeout'         // query took too long — narrow the range
  | 'rangeInvalid'    // bad range/selector (should be prevented by the controls)
  | 'notAvailable'    // scope errors — not available for this account
  | 'notAuthorized'   // platform VError 403
  | 'notEnabled'      // gateway flag off (plain 404, expected in local dev)
  | 'generic';

export interface AnalyticsErrorView {
  kind: AnalyticsErrorKind;
  title: string;
  detail?: string;
  availableFrom?: number;
  availableTo?: number;
  /** True for the pre-product-window case — the error state offers the Legacy Reports route. */
  legacyPointer?: boolean;
}

export interface CoverageNoticeView {
  message: string;
}

/**
 * Formatting and error-mapping helpers for the analytics feature.
 * Static class per repo convention.
 */
export class AnalyticsUtils {
  /** "4h 12m" style watch-time label from milliseconds. */
  static formatWatchTime(ms: number): string {
    if (!isFinite(ms) || ms <= 0) return '0m';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  /** Byte label using the platform's binary (1024-based) convention. */
  static formatBytes(bytes: number): string {
    if (!isFinite(bytes) || bytes < 0) return '0 B';
    let value = bytes;
    let unitIndex = 0;
    while (value >= BYTES_PER_UNIT && unitIndex < BYTE_UNITS.length - 1) {
      value /= BYTES_PER_UNIT;
      unitIndex += 1;
    }
    const label = unitIndex === 0 ? String(Math.round(value)) : value.toFixed(1);
    return `${label} ${BYTE_UNITS[unitIndex]}`;
  }

  /** Locale-grouped integer count. */
  static formatCount(value: number): string {
    if (!isFinite(value)) return '0';
    return Math.round(value).toLocaleString();
  }

  /** Whole-percent label from a 0..1 fraction ("42%"). */
  static formatPercent(fraction: number): string {
    if (!isFinite(fraction)) return '0%';
    return `${Math.round(fraction * 100)}%`;
  }

  /**
   * Custom-range label for period-scoped headings ("Jun 1 – Jun 30, 2026,
   * UTC"). Ranges are end-exclusive UTC calendar days, so the shown end day
   * is `to` minus one day (matching the range picker's display).
   */
  static formatUtcDayRangeLabel(range: UtcRange): string {
    const fmt = (seconds: number) =>
      DateTime.fromSeconds(seconds, { zone: 'utc' }).toFormat('MMM d, yyyy');
    return `${fmt(range.from)} – ${fmt(range.to - SECONDS_PER_DAY)}, UTC`;
  }

  /**
   * Compact inclusive UTC day span: "Apr 23 – Jul 22, 2026" within one year,
   * "Nov 12, 2025 – Jan 5, 2026" across years, "Jul 22, 2026" for one day.
   */
  static formatUtcDaySpan(firstDaySeconds: number, lastDaySeconds: number): string {
    const first = DateTime.fromSeconds(firstDaySeconds, { zone: 'utc' });
    const last = DateTime.fromSeconds(lastDaySeconds, { zone: 'utc' });
    if (first.hasSame(last, 'day')) return last.toFormat('MMM d, yyyy');
    if (first.year === last.year) {
      return `${first.toFormat('MMM d')} – ${last.toFormat('MMM d, yyyy')}`;
    }
    return `${first.toFormat('MMM d, yyyy')} – ${last.toFormat('MMM d, yyyy')}`;
  }

  /**
   * True when coverage starts after the selected range's first UTC day —
   * the range reaches back before collection began (vs. a gap at the end,
   * where data merely lags). Gates the Legacy Reports pointer.
   */
  static coverageGapAtStart(coverage: AnalyticsCoverage, range: UtcRange): boolean {
    const coverageDayStart = Math.floor(coverage.from / SECONDS_PER_DAY) * SECONDS_PER_DAY;
    return coverageDayStart > range.from;
  }

  /**
   * Decide the partial-coverage banner; null = show nothing. The ONLY case a
   * customer needs told about is history missing at the range START (days
   * before the account's data begins were genuinely unmeasured — the banner
   * routes eligible accounts to Legacy Reports). Trailing gaps are silent on
   * purpose: collection is always-on and daily materialization is same-day
   * (verified against prod 2026-07-23), so days the gateway omits at the end
   * are real zeros the chart already draws. That is why the covered span's
   * END is the selection end capped at TODAY — never `coverage.to` (the last
   * day with rows), which would re-surface a quiet tail as "data stops at X"
   * and invite the very question this banner exists to prevent.
   */
  static coverageNoticeView(
    coverage: AnalyticsCoverage,
    range: UtcRange,
    nowSeconds: number
  ): CoverageNoticeView | null {
    if (!coverage.partial || !this.coverageGapAtStart(coverage, range)) return null;
    const lastSelectedInstant = range.to - 1;
    const endOfTodayUtc =
      (Math.floor(nowSeconds / SECONDS_PER_DAY) + 1) * SECONDS_PER_DAY - 1;
    const covered = this.formatUtcDaySpan(
      Math.max(coverage.from, range.from),
      Math.min(lastSelectedInstant, endOfTodayUtc)
    );
    const selected = this.formatUtcDaySpan(range.from, lastSelectedInstant);
    return { message: `Data covers ${covered} of your selected range ${selected} (UTC).` };
  }

  /** A live snapshot built more than ~3 minutes ago is stale. */
  static isLiveStale(builtAtSeconds: number, nowSeconds: number): boolean {
    return nowSeconds - builtAtSeconds > ANALYTICS_LIVE_STALE_AFTER_SECONDS;
  }

  /**
   * Map a query error to a renderable view state — structured `code` first,
   * HTTP status second (plan error-handling table).
   */
  static errorView(error: unknown): AnalyticsErrorView {
    if (!(error instanceof ApiError)) {
      return { kind: 'generic', title: 'Analytics could not be loaded.' };
    }

    switch (error.code) {
      case 'range_outside_coverage':
        return {
          kind: 'empty',
          title: 'No analytics data for this range.',
          availableFrom: error.availableFrom,
          availableTo: error.availableTo,
        };
      case 'range_before_product_window':
        return {
          kind: 'empty',
          title: 'New Analytics starts later than this range.',
          detail: 'Pick a range on or after the analytics start date.',
          availableFrom: error.availableFrom,
          legacyPointer: true,
        };
      case 'overloaded':
      case 'downstream_unavailable':
        return {
          kind: 'busy',
          title: 'Analytics is busy — retrying shortly.',
          detail: 'Try again in a moment.',
        };
      case 'query_timeout':
      case 'gateway_timeout':
        return {
          kind: 'timeout',
          title: 'This query took too long.',
          detail: 'Narrow the date range and retry.',
        };
      case 'range_too_large':
      case 'point_cap_exceeded':
      case 'invalid_time_range':
      case 'invalid_bucket':
      case 'invalid_dimension':
      case 'invalid_metric':
      case 'invalid_limit':
      case 'invalid_cid':
      case 'invalid_media_id':
        return {
          kind: 'rangeInvalid',
          title: 'This request could not be processed.',
          detail: 'Adjust the date range or selection and retry.',
        };
      case 'scope_inactive':
      case 'scope_not_found':
      case 'scope_removed':
      case 'not_top_level':
      case 'invalid_parent':
        return { kind: 'notAvailable', title: 'Not available for this account.' };
      default:
        break;
    }

    if (error.httpStatusCode === 403) {
      // Platform VError (not authenticated/authorized) — HTTP 403, no auto-logout.
      return { kind: 'notAuthorized', title: 'You are not authorized to view analytics.' };
    }
    if (error.httpStatusCode === 404) {
      // Gateway flag off (e.g. dev against the production proxy until enablement).
      return { kind: 'notEnabled', title: 'Analytics is not enabled in this environment.' };
    }
    return { kind: 'generic', title: 'Analytics could not be loaded.' };
  }
}
